import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { buildLeaseContractPdfBytes, bytesToBase64, appendPdf, gstInclusive } from '../utils/leaseContract';
import { buildSubcontractorAgreementPdfBytes, fmtRate } from '../utils/subcontractorAgreement';
import { buildConditionReportPdfBytes, formToConditionData } from '../utils/conditionReport';
import { SignaturePad } from '../components/LeaseSignCard';
import ClientAgreementsTab from '../components/ClientAgreementsTab';
import {
  FileSignature, Eye, Download, CheckCircle, Send, Loader, Lock, Truck, AlertTriangle, PenLine, Save, Pencil, Ban, BellRing,
} from 'lucide-react';

const VEHICLE_ADMIN_URL = '/.netlify/functions/vehicle-admin-sb';
const CONTRACT_URL = '/.netlify/functions/vehicle-contract-sb';

// HPL (Lessor) identity comes from Settings → Company Details (the "Asset" company),
// stored in localStorage. Falls back to the known defaults.
const COMPANY_STORE = 'hp-company-details';
function hplDefaults() {
  try {
    const c = JSON.parse(localStorage.getItem(COMPANY_STORE) || '{}');
    return { name: c.assetCompany || 'Hollypaul Leasing Pty Ltd', abn: c.assetAbn || '29 522 075 794' };
  } catch { return { name: 'Hollypaul Leasing Pty Ltd', abn: '29 522 075 794' }; }
}

const toLocalISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Find the vehicle a driver is currently assigned to (fleet.login → driver.loginName).
function vehicleForDriver(driver, vehicles) {
  if (!driver) return null;
  const login = (driver.loginName || '').toUpperCase();
  if (!login) return null;
  return vehicles.find(v => !v.archived && (v.login || '').toUpperCase() === login) || null;
}

// ── Lessor (HPL) signature setup — saved once, stamped onto every contract ─────
function LessorSignatureModal({ initial, onClose, onSaved, addNotification }) {
  const [sig, setSig] = useState('');
  const [name, setName] = useState(initial?.name || '');
  const [position, setPosition] = useState(initial?.position || 'Director');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!sig) { addNotification('Add a signature first', 'error'); return; }
    if (!name.trim()) { addNotification('Enter the signatory name', 'error'); return; }
    setSaving(true);
    try {
      const res = await fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({ action: 'saveLessorSignature', signature: sig, name, position }) }).then(r => r.json());
      if (res.success) { addNotification('Lessor signature saved', 'success'); onSaved({ signature: sig, name, position }); onClose(); }
      else addNotification(`Save failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); } finally { setSaving(false); }
  };

  const inp = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '0.9rem', boxSizing: 'border-box' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '32px 12px', overflowY: 'auto' }}>
      <div style={{ width: 560, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Lessor signature — Hollypaul Leasing</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ margin: '0 0 14px', fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
            Saved once and applied to every contract — so each lease goes out already signed by Hollypaul Leasing. Update it any time.
          </p>
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', display: 'block', marginBottom: 5 }}>Signatory name</label>
              <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Paul Bouveng" />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', display: 'block', marginBottom: 5 }}>Position</label>
              <input style={inp} value={position} onChange={e => setPosition(e.target.value)} placeholder="e.g. Director" />
            </div>
          </div>
          <label style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', display: 'block', marginBottom: 6 }}>Signature</label>
          <SignaturePad onChange={setSig} defaultName={name} />
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={saving} onClick={save}>
            {saving ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />} Save signature
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Vehicle Lease tab — generates the HPL → driver lease as a PDF and files it ──
function VehicleLeaseTab({ initialDriverLogin }) {
  const { personnel, vehicles, currentUser, addNotification } = useApp();
  // `personnel` IS the drivers list (loaded as data.drivers); `type` here is the
  // delivery type (Parcel/Express), not a role — so filter on having a login.
  const drivers = useMemo(
    () => personnel.filter(p => p.loginName).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [personnel],
  );

  const hpl = hplDefaults();
  const [driverId, setDriverId]   = useState('');
  const [weeklyFee, setWeeklyFee] = useState('');
  const [excess, setExcess]       = useState('2500');
  const [hplAbn, setHplAbn]       = useState(hpl.abn);
  const [term, setTerm]           = useState('');
  const [startDate, setStartDate] = useState(toLocalISO(new Date()));
  const [busy, setBusy]           = useState('');
  const [lessorSig, setLessorSig] = useState(null);     // saved HPL signature {signature,name,position}
  const [showLessorSetup, setShowLessorSetup] = useState(false);

  useEffect(() => {
    fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({ action: 'getLessorSignature' }) })
      .then(r => r.json()).then(d => { if (d.success) setLessorSig(d.signature || null); }).catch(() => {});
  }, []);

  // Deep-linked from Personnel "Generate" → pre-select that driver once the list is in.
  useEffect(() => {
    if (initialDriverLogin && drivers.some(d => d.loginName === initialDriverLogin)) setDriverId(initialDriverLogin);
  }, [initialDriverLogin, drivers]);

  const driver = drivers.find(d => d.loginName === driverId) || null;
  const vehicle = useMemo(() => vehicleForDriver(driver, vehicles), [driver, vehicles]);

  // A lease must carry the lessee's real legal name + ABN — never a login.
  const lesseeNameMissing = !!driver && (!driver.name || driver.name.trim().toUpperCase() === (driver.loginName || '').toUpperCase());
  const lesseeAbnMissing = !!driver && !driver.abn;

  // The latest Condition Report on file for the resolved vehicle (id used to attach it).
  const [hasCondition, setHasCondition] = useState(null); // null = unknown/checking
  const [conditionDoc, setConditionDoc] = useState(null);
  useEffect(() => {
    if (!vehicle?.rego) { setHasCondition(null); setConditionDoc(null); return; }
    let alive = true; setHasCondition(null); setConditionDoc(null);
    fetch(VEHICLE_ADMIN_URL, { method: 'POST', body: JSON.stringify({ action: 'getVehicleDocuments', rego: vehicle.rego }) })
      .then(r => r.json())
      .then(d => { if (!alive) return; const cr = (d.documents || []).find(x => x.category === 'Condition Report') || null; setConditionDoc(cr); setHasCondition(!!cr); })
      .catch(() => { if (alive) { setHasCondition(null); setConditionDoc(null); } });
    return () => { alive = false; };
  }, [vehicle?.rego]);

  // Append the on-file condition report so the lease + report go out as one document.
  // Prefer the structured data so the Lessor signature is stamped on the report too;
  // fall back to the static filed PDF for legacy reports.
  const withConditionReport = async (leaseBytes) => {
    if (!conditionDoc?.id) return leaseBytes;
    try {
      const d = await fetch(VEHICLE_ADMIN_URL, { method: 'POST', body: JSON.stringify({ action: 'getConditionReport', docId: conditionDoc.id }) }).then(r => r.json());
      if (d.success && d.report) {
        const crData = formToConditionData(d.report, vehicle);
        crData.lessorSignature = lessorSig?.signature || null;
        crData.lessorSignatory = lessorSig?.name || '';
        crData.lessorPosition = lessorSig?.position || '';
        crData.lessorSignedDate = lessorSig?.signature ? toLocalISO(new Date()) : '';
        return await appendPdf(leaseBytes, await buildConditionReportPdfBytes(crData));
      }
      const res = await fetch(`/.netlify/functions/vehicle-doc-sb?id=${conditionDoc.id}`);
      if (!res.ok) return leaseBytes;
      return await appendPdf(leaseBytes, new Uint8Array(await res.arrayBuffer()));
    } catch { return leaseBytes; }
  };

  // When the driver changes, default the weekly fee from their personnel record.
  useEffect(() => {
    if (!driver) { setWeeklyFee(''); return; }
    setWeeklyFee(driver.leaseFee ? String(driver.leaseFee) : '');
  }, [driverId, driver]);

  // Mirror the RCTI's precedence (RCTIPage buildRCTI): the % rate wins whenever it is
  // set, even if a fixed fee is ALSO on the record — the fixed figure is then ignored.
  // So a driver counts as variable-rate purely on leaseRate > 0.
  const onPercentRate = !!driver && driver.leaseRate > 0;
  const leaseCapNum = Number(driver?.leaseCap) || 0;

  const buildData = () => ({
    lessorName: (hpl.name || 'HOLLYPAUL LEASING PTY LTD').toUpperCase(),
    lessorAbn: hplAbn || '[INSERT ABN]',
    lessee: { entity: driver?.companyName || driver?.name || '', repName: driver?.name || '', abn: driver?.abn || '' },
    vehicle: vehicle || {},
    startDate,
    term: term || undefined,
    weeklyFee: Number(weeklyFee) || 0,
    leaseRate: onPercentRate ? Number(driver.leaseRate) || 0 : 0,
    leaseCap: onPercentRate ? leaseCapNum : 0,
    excessBasic: Number(excess) || 2500,
    state: 'New South Wales',
    lessorSignature: lessorSig?.signature || null,
    lessorSignatory: lessorSig?.name || '',
    lessorPosition: lessorSig?.position || '',
    lessorSignedDate: lessorSig?.signature ? toLocalISO(new Date()) : '',
  });

  const makeBytes = () => buildLeaseContractPdfBytes(buildData());
  const fileStem = () => `Lease-${vehicle?.rego || 'vehicle'}-${(driver?.name || 'driver').replace(/\s+/g, '')}`;

  const handlePreview = async () => {
    // Open the tab synchronously inside the click gesture, THEN fill it once the
    // PDF is built — opening after the await gets silently blocked as a popup.
    const win = window.open('', '_blank');
    setBusy('preview');
    try {
      const bytes = await withConditionReport(await makeBytes());
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) { win.location.href = url; }
      else {
        // popups blocked — fall back to a download
        const a = document.createElement('a');
        a.href = url; a.download = `${fileStem()}.pdf`; a.click();
        addNotification('Preview opened as a download (allow pop-ups to view in a tab)', 'info');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      addNotification(`Couldn’t build the preview: ${e.message}`, 'error');
    }
    finally { setBusy(''); }
  };

  const handleDownload = async () => {
    setBusy('download');
    try {
      const bytes = await withConditionReport(await makeBytes());
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${fileStem()}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { addNotification(`Download failed: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const handleFile = async () => {
    if (!vehicle) { addNotification('No vehicle resolved for this driver', 'error'); return; }
    setBusy('file');
    try {
      const bytes = await withConditionReport(await makeBytes());
      const fileBase64 = bytesToBase64(bytes);
      const res = await fetch(VEHICLE_ADMIN_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'saveVehicleDocument',
          rego: (vehicle.rego || '').toUpperCase(),
          category: 'Finance / Lease',
          label: `Lease agreement — ${driver?.name || ''}`.trim(),
          filename: `${fileStem()}.pdf`,
          contentType: 'application/pdf',
          fileBase64,
          extracted: { summary: `HPL vehicle lease for ${driver?.name} — ${vehicle.rego}`, weeklyFee: Number(weeklyFee) || 0 },
          uploadedBy: currentUser?.login || '',
        }),
      }).then(r => r.json());
      if (res.success) addNotification(`Lease filed to ${vehicle.rego} documents`, 'success');
      else addNotification(`Filing failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const handleSend = async () => {
    if (!vehicle) { addNotification('No vehicle resolved for this driver', 'error'); return; }
    if (!driver?.loginName) { addNotification('This driver has no login — set one on the Personnel page', 'error'); return; }
    if (lesseeNameMissing) { addNotification('This driver has no real legal name in Personnel (it shows the login). Set their full name on the Personnel page before sending.', 'error'); return; }
    if (lesseeAbnMissing && !window.confirm('This driver has no ABN on record. Send the lease without an ABN?')) return;
    if (!lessorSig?.signature && !window.confirm('No Lessor (Hollypaul Leasing) signature is on file, so the contract will go out unsigned by HPL. Set it now via "Lessor signature", or send anyway?')) return;
    if (hasCondition === false && !window.confirm(`No vehicle condition report is on file for ${vehicle.rego}. It's recommended to complete one (Fleet → Docs → New condition report) before leasing, so the driver isn't charged for pre-existing damage. Send anyway?`)) return;
    setBusy('send');
    const send = (allowDuplicate) => fetch(CONTRACT_URL, {
      method: 'POST',
      body: JSON.stringify({
        action: 'createContract',
        rego: (vehicle.rego || '').toUpperCase(),
        driverLogin: driver.loginName,
        driverName: driver.name || '',
        driverEntity: driver.companyName || driver.name || '',
        driverAbn: driver.abn || '',
        weeklyFee: Number(weeklyFee) || 0,
        leaseRate: onPercentRate ? Number(driver.leaseRate) || 0 : 0,
        leaseCap: onPercentRate ? leaseCapNum : 0,
        excessBasic: Number(excess) || 2500,
        startDate, term: term || '',
        lessorName: (hpl.name || 'HOLLYPAUL LEASING PTY LTD').toUpperCase(),
        lessorAbn: hplAbn || '',
        vehicle: { year: vehicle.year, make: vehicle.make, model: vehicle.model, rego: vehicle.rego },
        conditionReportDocId: conditionDoc?.id || '',
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
      }),
    }).then(r => r.json());
    try {
      let res = await send(false);
      if (res.duplicate && window.confirm(`${res.error}\n\nCreate another pending lease anyway?`)) {
        res = await send(true);
      }
      if (res.success) addNotification(`Sent to ${driver.name} — they’ll sign it in their portal (My Van)`, 'success');
      else if (!res.duplicate) addNotification(`Send failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const labelStyle = { fontSize: '0.78rem', fontWeight: 500, color: 'var(--hp-dim)', display: 'block', marginBottom: 5 };
  const ready = !!driver && !!vehicle;

  return (
    <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 24, alignItems: 'flex-start' }}>
      <div className="card">
        <h4 style={{ marginBottom: 4 }}>Generate Lease Agreement</h4>
        <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 14 }}>Hollypaul Leasing Pty Ltd → Driver</p>

        {/* Lessor signature status — pre-signed onto every contract */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', marginBottom: 16, borderRadius: 8, background: 'var(--hp-surface)', border: `1px solid ${lessorSig?.signature ? 'var(--hp-border)' : 'var(--hp-amber)'}` }}>
          {lessorSig?.signature
            ? <CheckCircle size={15} color="var(--hp-green)" />
            : <AlertTriangle size={15} color="var(--hp-amber)" />}
          <span style={{ fontSize: '0.8rem', flex: 1 }}>
            Lessor signature: {lessorSig?.signature ? `on file (${lessorSig.name || 'HPL'})` : 'not set'}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowLessorSetup(true)}>
            <PenLine size={12} /> {lessorSig?.signature ? 'Update' : 'Set signature'}
          </button>
        </div>

        <div className="form-group">
          <label style={labelStyle}>Driver / Lessee</label>
          <select className="form-select" value={driverId} onChange={e => setDriverId(e.target.value)}>
            <option value="">— Choose driver —</option>
            {drivers.map(d => <option key={d.loginName} value={d.loginName}>{d.name}{d.abn ? ` (ABN ${d.abn})` : ''}</option>)}
          </select>
        </div>

        {/* Auto-resolved vehicle */}
        {driver && (
          <div style={{ margin: '4px 0 16px', padding: '10px 12px', borderRadius: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)' }}>
            {vehicle ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem' }}>
                <Truck size={15} color="var(--hp-gold)" />
                <span>Currently driving <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{vehicle.rego}</strong> — {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}</span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--hp-amber)' }}>
                <AlertTriangle size={14} /> No vehicle assigned to this driver in the fleet — assign one on the Fleet page first.
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label style={labelStyle}>Lease fee per 4 weeks (ex GST — matches the RCTI)</label>
          <input className="form-input" type="number" value={weeklyFee} onChange={e => setWeeklyFee(e.target.value)} placeholder="e.g. 350.00" disabled={onPercentRate} />
          {onPercentRate && (
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-amber)', marginTop: 4 }}>
              This driver is on {(driver.leaseRate * 100).toFixed(1)}% of gross{leaseCapNum > 0 ? ` (capped at $${gstInclusive(leaseCapNum).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} incl. GST)` : ''} — the contract states that variable arrangement, not a fixed fee, so this figure isn’t used.
            </div>
          )}
        </div>

        <div className="form-group">
          <label style={labelStyle}>Insurance excess (basic, at-fault)</label>
          <input className="form-input" type="number" value={excess} onChange={e => setExcess(e.target.value)} placeholder="2500" />
        </div>

        <div className="form-group">
          <label style={labelStyle}>Hollypaul Leasing ABN</label>
          <input className="form-input" value={hplAbn} onChange={e => setHplAbn(e.target.value)} placeholder="HPL ABN" />
        </div>

        <div className="form-group">
          <label style={labelStyle}>Lease start date</label>
          <input className="form-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>

        <div className="form-group">
          <label style={labelStyle}>Lease term</label>
          <input className="form-input" value={term} onChange={e => setTerm(e.target.value)} placeholder="Ongoing — until terminated" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <button className="btn btn-primary w-full" style={{ justifyContent: 'center' }} disabled={!ready || !!busy} onClick={handleSend}>
            {busy === 'send' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />} Send for e-signature
          </button>
          <button className="btn btn-secondary w-full" style={{ justifyContent: 'center' }} disabled={!ready || !!busy} onClick={handlePreview}>
            {busy === 'preview' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Eye size={14} />} Preview PDF
          </button>
          <button className="btn btn-ghost w-full" style={{ justifyContent: 'center' }} disabled={!ready || !!busy} onClick={handleDownload}>
            {busy === 'download' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />} Download PDF
          </button>
          <button className="btn btn-ghost w-full" style={{ justifyContent: 'center' }} disabled={!ready || !!busy} onClick={handleFile}>
            {busy === 'file' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Lock size={14} />} File unsigned copy to documents
          </button>
        </div>
      </div>

      {showLessorSetup && (
        <LessorSignatureModal
          initial={lessorSig}
          onClose={() => setShowLessorSetup(false)}
          onSaved={(s) => setLessorSig(s)}
          addNotification={addNotification}
        />
      )}

      {/* Summary / what goes in */}
      <div className="card" style={{ background: 'var(--hp-surface)' }}>
        <h4 style={{ marginBottom: 16 }}>What the contract carries</h4>
        {!driver ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--hp-muted)' }}>
            <FileSignature size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p style={{ fontSize: '0.875rem' }}>Choose a driver — the contract auto-builds from the rego they currently drive.</p>
          </div>
        ) : (
          <div style={{ fontSize: '0.84rem', lineHeight: 1.8, color: 'var(--hp-text)' }}>
            <div><strong>Lessor:</strong> Hollypaul Leasing Pty Ltd{hplAbn ? ` (ABN ${hplAbn})` : ''}</div>
            <div><strong>Lessee (ABN holder):</strong> {driver.companyName || driver.name}{driver.abn ? ` · ABN ${driver.abn}` : ''}</div>
            <div><strong>Signed by:</strong> {driver.name}{driver.companyName ? ' (on behalf of the entity)' : ''}</div>
            {lesseeNameMissing && (
              <div style={{ color: 'var(--hp-red)', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <AlertTriangle size={13} /> No real name on this driver — it shows the login. Fix it on Personnel before sending.
              </div>
            )}
            {!lesseeNameMissing && lesseeAbnMissing && (
              <div style={{ color: 'var(--hp-amber)', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <AlertTriangle size={13} /> No ABN on record for this driver.
              </div>
            )}
            <div><strong>Vehicle:</strong> {vehicle ? `${[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')} — ${vehicle.rego}` : '— none assigned —'}</div>
            {vehicle && (
              <div><strong>Condition report:</strong> {hasCondition == null ? '—' : hasCondition ? 'on file ✓' : <span style={{ color: 'var(--hp-amber)' }}>not yet — recommended before leasing</span>}</div>
            )}
            <div><strong>Lease fee (per 4 weeks):</strong> {onPercentRate
              ? `${(driver.leaseRate * 100).toFixed(1)}% of gross${leaseCapNum > 0 ? `, capped at $${gstInclusive(leaseCapNum).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} incl. GST ($${leaseCapNum.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ex GST)` : ''}`
              : weeklyFee ? `$${Number(weeklyFee).toFixed(2)} + GST ($${(Number(weeklyFee) * 1.1).toFixed(2)} incl.)` : '—'}</div>
            <div><strong>At-fault excess:</strong> ${Number(excess || 0).toLocaleString()} basic (+$850/$500 age, +$500 inexperienced)</div>
            <hr style={{ border: 'none', borderTop: '1px solid var(--hp-border)', margin: '12px 0' }} />
            <div style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>
              Includes: responsibility split (HPL covers insurance, servicing, rego, fair-wear tyres; driver covers fuel, fines, cleaning, at-fault excess, puncture/kerb tyre damage), the wear-and-tear return policy, and the insurance inclusions (hire car after accident, windscreen nil-excess, towing, re-keying).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Shared modal shell — matches LessorSignatureModal.
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '32px 12px', overflowY: 'auto' };
const modalCard = { width: 520, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' };
const modalInput = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '0.9rem', boxSizing: 'border-box' };
const modalLabel = { fontSize: '0.78rem', color: 'var(--hp-dim)', display: 'block', marginBottom: 5 };
const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Amend the schedule (pending contracts only) ────────────────────────────────
function AmendContractModal({ contract, onClose, onSaved, addNotification }) {
  // A variable-rate lease is amended on its rate/cap, not a flat fee (the fee is ignored).
  const isPct = Number(contract.leaseRate) > 0;
  const [weeklyFee, setWeeklyFee] = useState(contract.weeklyFee != null ? String(contract.weeklyFee) : '');
  const [leaseRate, setLeaseRate] = useState(contract.leaseRate != null ? String(contract.leaseRate) : '');
  const [leaseCap, setLeaseCap]   = useState(contract.leaseCap != null ? String(contract.leaseCap) : '');
  const [excess, setExcess]       = useState(contract.excessBasic != null ? String(contract.excessBasic) : '2500');
  const [startDate, setStartDate] = useState((contract.startDate || '').slice(0, 10));
  const [term, setTerm]           = useState(contract.term || '');
  const [note, setNote]           = useState('');
  const [saving, setSaving]       = useState(false);

  const save = async () => {
    const fee = Number(weeklyFee);
    const rate = Number(leaseRate);
    if (isPct ? !(rate > 0) : !(fee > 0)) { addNotification(isPct ? 'Enter a valid lease rate' : 'Enter a valid lease fee', 'error'); return; }
    setSaving(true);
    try {
      const res = await fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({
        action: 'amendContract', id: contract.id,
        ...(isPct ? { leaseRate: rate, leaseCap: Number(leaseCap) || 0 } : { weeklyFee: fee }),
        excessBasic: Number(excess) || 0, startDate, term, note,
      }) }).then(r => r.json());
      if (res.success) {
        addNotification(res.unchanged ? 'No changes to save' : 'Schedule amended — the driver signs the corrected figures', res.unchanged ? 'info' : 'success');
        onSaved(); onClose();
      } else addNotification(`Amend failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); } finally { setSaving(false); }
  };

  const feeNum = Number(weeklyFee) || 0;
  return (
    <div style={modalOverlay}>
      <div style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Amend schedule — {contract.driverName || contract.driverLogin}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ margin: '0 0 14px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
            {contract.vehicle?.rego || contract.rego} · still pending signature. Corrections here are what the driver will sign — nothing is signed yet.
          </p>
          {isPct ? (
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={modalLabel}>Lease rate (%)</label>
                <input style={modalInput} type="number" step="0.01" value={leaseRate} onChange={e => setLeaseRate(e.target.value)} placeholder="0.15" />
                {Number(leaseRate) > 0 && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>{(Number(leaseRate) * 100).toFixed(1)}% of gross</div>
                )}
              </div>
              <div>
                <label style={modalLabel}>Lease cap ($, ex GST)</label>
                <input style={modalInput} type="number" step="0.01" value={leaseCap} onChange={e => setLeaseCap(e.target.value)} placeholder="1050" />
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <label style={modalLabel}>Lease fee per 4 weeks (ex GST — matches the RCTI)</label>
              <input style={modalInput} type="number" value={weeklyFee} onChange={e => setWeeklyFee(e.target.value)} placeholder="e.g. 1181.80" />
              {feeNum > 0 && (
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                  ${feeNum.toFixed(2)} + GST (${(feeNum * 1.1).toFixed(2)} incl.) every 4 weeks
                </div>
              )}
            </div>
          )}
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={modalLabel}>Insurance excess (basic)</label>
              <input style={modalInput} type="number" value={excess} onChange={e => setExcess(e.target.value)} placeholder="2500" />
            </div>
            <div>
              <label style={modalLabel}>Lease start date</label>
              <input style={modalInput} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={modalLabel}>Lease term</label>
            <input style={modalInput} value={term} onChange={e => setTerm(e.target.value)} placeholder="Ongoing — until terminated" />
          </div>
          <div>
            <label style={modalLabel}>Note (optional — kept in the amendment history)</label>
            <input style={modalInput} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. corrected 4-weekly fee" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={saving} onClick={save}>
            {saving ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />} Save amendment
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Withdraw a contract (retained for legal reasons, with a reason + comment) ───
function WithdrawContractModal({ contract, onClose, onSaved, addNotification }) {
  const [reason, setReason]   = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving]   = useState(false);
  const signed = contract.status === 'signed';

  const save = async () => {
    if (!reason.trim()) { addNotification('Enter a reason for withdrawal', 'error'); return; }
    setSaving(true);
    try {
      const res = await fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({
        action: 'withdrawContract', id: contract.id, reason: reason.trim(), comment,
      }) }).then(r => r.json());
      if (res.success) { addNotification('Contract withdrawn — retained on file', 'success'); onSaved(); onClose(); }
      else addNotification(`Withdraw failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); } finally { setSaving(false); }
  };

  return (
    <div style={modalOverlay}>
      <div style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Withdraw contract — {contract.driverName || contract.driverLogin}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 8, padding: '10px 12px', marginBottom: 14, borderRadius: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
            <AlertTriangle size={15} color="var(--hp-amber)" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              The contract is <strong>retained on file</strong> for the record — withdrawing only marks it inactive.
              {signed ? ' The signed PDF stays filed against the vehicle.' : ' It will disappear from the driver’s portal.'}
            </span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={modalLabel}>Reason for withdrawal (required)</label>
            <input style={modalInput} value={reason} onChange={e => setReason(e.target.value)} list="withdraw-reasons" placeholder="e.g. Incorrect fee — re-issuing" />
            <datalist id="withdraw-reasons">
              <option value="Incorrect fee — re-issuing" />
              <option value="Wrong vehicle" />
              <option value="Driver left" />
              <option value="Terms changed" />
              <option value="Superseded by a new contract" />
              <option value="Created in error" />
            </datalist>
          </div>
          <div>
            <label style={modalLabel}>Comment (optional)</label>
            <textarea style={{ ...modalInput, minHeight: 70, resize: 'vertical' }} value={comment} onChange={e => setComment(e.target.value)} placeholder="Any extra detail for the record…" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
          <button className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }} disabled={saving} onClick={save}>
            {saving ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Ban size={14} />} Withdraw contract
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Contract Register — live statuses from the contract store ──────────────────
function ContractRegister() {
  const { personnel, addNotification } = useApp();
  const [rows, setRows] = useState(null);
  const [amendTarget, setAmendTarget]       = useState(null);
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [remindingId, setRemindingId]       = useState(null);

  // Re-push a pending lease to the driver: SMS via the live send-sms path (the portal
  // red dot is passive — nothing else chases them), then stamp the nudge on the
  // contract record so the register shows when they were last reminded.
  const remind = async (c) => {
    const login = (c.driverLogin || '').toUpperCase();
    const person = (personnel || []).find(p => (p.loginName || '').toUpperCase() === login);
    const phone = (person?.phone || '').trim();
    if (!phone) { addNotification(`No mobile on file for ${c.driverName || login} — add one in Personnel first.`, 'error'); return; }
    const first = (c.driverName || '').trim().split(/\s+/)[0] || 'there';
    const rego = c.vehicle?.rego || c.rego;
    const msg = `Hi ${first}, a reminder from Hollypaul: your van lease agreement for ${rego} is still waiting for your signature. Please log in to the driver portal at hollypaul.com/driver-log, open the My Van tab and sign. Thank you.`;
    if (!window.confirm(`Text ${c.driverName || login} (${phone}) this reminder?\n\n"${msg}"`)) return;
    setRemindingId(c.id);
    let ok = false, sid = '', error = '';
    try {
      const r = await fetch('/.netlify/functions/send-sms', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: phone, message: msg }),
      }).then(x => x.json());
      ok = r.ok === true; sid = r.sid || ''; error = r.error || '';
    } catch (e) { error = e.message; }
    try {
      await fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({ action: 'remindContract', id: c.id, to: phone, ok, sid, error }) });
    } catch { /* the send outcome matters more than the audit stamp */ }
    setRemindingId(null);
    addNotification(ok ? `Reminder sent to ${c.driverName || login}` : `Reminder SMS failed: ${error || 'unknown error'}`, ok ? 'success' : 'error');
    load();
  };

  const load = useCallback(() => {
    fetch(CONTRACT_URL, { method: 'POST', body: JSON.stringify({ action: 'getContracts' }) })
      .then(r => r.json())
      .then(d => { if (d.success) setRows(d.contracts || []); else { setRows([]); addNotification(`Could not load contracts: ${d.error || ''}`, 'error'); } })
      .catch(e => { setRows([]); addNotification(`Error: ${e.message}`, 'error'); });
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  const badge = (s) => s === 'signed'
    ? <span className="badge badge-green">Signed</span>
    : s === 'withdrawn'
      ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, background: 'rgba(245,158,11,0.16)', color: 'var(--hp-amber)' }}>Withdrawn</span>
      : s === 'void'
        ? <span className="badge badge-red">Void</span>
        : s === 'declined'
          ? <span className="badge badge-red">Declined</span>
          : <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, background: 'rgba(212,175,55,0.18)', color: 'var(--hp-gold)' }}>Pending signature</span>;

  if (rows === null) return <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-muted)' }}><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>;
  if (!rows.length) return <div className="card" style={{ padding: '34px', textAlign: 'center', color: 'var(--hp-muted)' }}>No contracts yet — generate one on the Vehicle Lease Agreements tab.</div>;

  return (
    <div className="card">
      <table className="data-table">
        <thead><tr><th>Driver</th><th>Vehicle</th><th>Fee / 4 wks</th><th>Status</th><th>Sent</th><th>Signed</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.map(c => {
            const retireReason = c.status === 'withdrawn' ? c.withdrawReason : c.status === 'declined' ? c.declineReason : '';
            return (
            <tr key={c.id}>
              <td style={{ fontWeight: 500 }}>{c.driverName || c.driverLogin}</td>
              <td><span className="font-mono" style={{ fontSize: '0.8rem', color: 'var(--hp-gold)' }}>{c.vehicle?.rego || c.rego}</span></td>
              <td style={{ fontSize: '0.82rem' }}>
                {c.leaseRate > 0
                  ? `${(c.leaseRate * 100).toFixed(1)}%${c.leaseCap > 0 ? ` ≤$${gstInclusive(c.leaseCap).toLocaleString('en-AU')} inc` : ''}`
                  : c.weeklyFee ? `$${Number(c.weeklyFee).toFixed(2)}` : '—'}
                {c.amendedAt && <span title={`Amended ${fmtDate(c.amendedAt)}`} style={{ marginLeft: 5, fontSize: '0.68rem', color: 'var(--hp-dim)' }}>· amended</span>}
              </td>
              <td>
                {badge(c.status)}
                {retireReason && <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 3, maxWidth: 180 }} title={c.withdrawComment || ''}>{retireReason}</div>}
              </td>
              <td style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
                {fmtDate(c.createdAt)}
                {c.lastRemindedAt && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--hp-gold)', marginTop: 2 }} title={`Reminder SMS sent ${(c.reminders || []).filter(r => r.ok).length} time(s)`}>
                    Reminded {fmtDate(c.lastRemindedAt)}{(c.reminders || []).filter(r => r.ok).length > 1 ? ` ×${(c.reminders || []).filter(r => r.ok).length}` : ''}
                  </div>
                )}
              </td>
              <td style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{fmtDate(c.signedAt)}</td>
              <td>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {c.status === 'signed' && c.signedDocId && (
                    <a href={`/.netlify/functions/vehicle-doc-sb?id=${c.signedDocId}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><Eye size={12} /> View signed</a>
                  )}
                  {c.status === 'pending' && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--hp-gold)' }} disabled={remindingId === c.id} onClick={() => remind(c)}>
                      {remindingId === c.id ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <BellRing size={12} />} Remind
                    </button>
                  )}
                  {c.status === 'pending' && (
                    <button className="btn btn-ghost btn-sm" onClick={() => setAmendTarget(c)}><Pencil size={12} /> Amend</button>
                  )}
                  {(c.status === 'pending' || c.status === 'signed') && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--hp-red)' }} onClick={() => setWithdrawTarget(c)}><Ban size={12} /> Withdraw</button>
                  )}
                  {c.status === 'withdrawn' && c.signedDocId && (
                    <a href={`/.netlify/functions/vehicle-doc-sb?id=${c.signedDocId}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><Eye size={12} /> View signed</a>
                  )}
                  {c.status !== 'signed' && c.status !== 'pending' && !(c.status === 'withdrawn' && c.signedDocId) && (
                    <span style={{ color: 'var(--hp-muted)', fontSize: '0.78rem' }}>—</span>
                  )}
                </div>
              </td>
            </tr>
          );})}
        </tbody>
      </table>

      {amendTarget && (
        <AmendContractModal contract={amendTarget} onClose={() => setAmendTarget(null)} onSaved={load} addNotification={addNotification} />
      )}
      {withdrawTarget && (
        <WithdrawContractModal contract={withdrawTarget} onClose={() => setWithdrawTarget(null)} onSaved={load} addNotification={addNotification} />
      )}
    </div>
  );
}

// ══ Subcontractor Agreements ═══════════════════════════════════════════════════
const AGREEMENT_URL = '/.netlify/functions/subcontractor-agreement-sb';

const addMonthsISO = (iso, months) => {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d)) return '';
  d.setMonth(d.getMonth() + months);
  return toLocalISO(d);
};

// ── Subcontractor register — pending / signed / withdrawn, live from the store ──
function SubcontractorRegister({ tick }) {
  const { addNotification } = useApp();
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(() => {
    fetch(AGREEMENT_URL, { method: 'POST', body: JSON.stringify({ action: 'getAgreements' }) })
      .then(r => r.json())
      .then(d => { if (d.success) setRows(d.agreements || []); else { setRows([]); addNotification(`Could not load agreements: ${d.error || ''}`, 'error'); } })
      .catch(e => { setRows([]); addNotification(`Error: ${e.message}`, 'error'); });
  }, [addNotification]);

  useEffect(() => { load(); }, [load, tick]);

  const viewSigned = async (c) => {
    setBusyId(c.id);
    const win = window.open('', '_blank');
    try {
      const d = await fetch(AGREEMENT_URL, { method: 'POST', body: JSON.stringify({ action: 'getSignedPdf', id: c.id }) }).then(r => r.json());
      if (!d.success) { if (win) win.close(); addNotification(`Could not open: ${d.error || 'unknown error'}`, 'error'); return; }
      const bytes = Uint8Array.from(atob(d.pdfBase64), ch => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) win.location.href = url; else { const a = document.createElement('a'); a.href = url; a.download = d.filename || 'signed-agreement.pdf'; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); addNotification(`Error: ${e.message}`, 'error'); }
    finally { setBusyId(''); }
  };

  const withdraw = async (c) => {
    const reason = window.prompt(`Withdraw the agreement for ${c.subEntity || c.driverName}? Enter a reason (kept on file):`, '');
    if (reason === null) return;
    if (!reason.trim()) { addNotification('A reason is required to withdraw', 'error'); return; }
    try {
      const res = await fetch(AGREEMENT_URL, { method: 'POST', body: JSON.stringify({ action: 'withdrawAgreement', id: c.id, reason: reason.trim() }) }).then(r => r.json());
      if (res.success) { addNotification('Agreement withdrawn — retained on file', 'success'); load(); }
      else addNotification(`Withdraw failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); }
  };

  const badge = (s) => s === 'signed'
    ? <span className="badge badge-green">Signed</span>
    : s === 'withdrawn'
      ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, background: 'rgba(245,158,11,0.16)', color: 'var(--hp-amber)' }}>Withdrawn</span>
      : s === 'void' ? <span className="badge badge-red">Void</span>
        : s === 'declined' ? <span className="badge badge-red">Declined</span>
          : <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, background: 'rgba(212,175,55,0.18)', color: 'var(--hp-gold)' }}>Pending signature</span>;

  if (rows === null) return <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-muted)' }}><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>;
  if (!rows.length) return <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--hp-muted)' }}>No subcontractor agreements yet — generate one above.</div>;

  return (
    <div className="card">
      <table className="data-table">
        <thead><tr><th>Subcontractor</th><th>Driver</th><th>Base rate</th><th>Status</th><th>Sent</th><th>Signed</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.map(c => {
            const retireReason = c.status === 'withdrawn' ? c.withdrawReason : c.status === 'declined' ? c.declineReason : '';
            return (
              <tr key={c.id}>
                <td style={{ fontWeight: 500 }}>{c.subEntity || '—'}</td>
                <td style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>{c.driverName || c.driverLogin}</td>
                <td style={{ fontSize: '0.82rem' }}>{c.baseRate ? `$${Number(c.baseRate).toFixed(2)}/parcel` : '—'}{c.amendedAt && <span style={{ marginLeft: 5, fontSize: '0.68rem', color: 'var(--hp-dim)' }}>· amended</span>}</td>
                <td>{badge(c.status)}{retireReason && <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 3, maxWidth: 180 }}>{retireReason}</div>}</td>
                <td style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{fmtDate(c.createdAt)}</td>
                <td style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{fmtDate(c.signedAt)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {c.status === 'signed' && (
                      <button className="btn btn-ghost btn-sm" disabled={busyId === c.id} onClick={() => viewSigned(c)}>
                        {busyId === c.id ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Eye size={12} />} View signed
                      </button>
                    )}
                    {c.status === 'pending' && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--hp-red)' }} onClick={() => withdraw(c)}><Ban size={12} /> Withdraw</button>
                    )}
                    {c.status !== 'signed' && c.status !== 'pending' && (
                      <span style={{ color: 'var(--hp-muted)', fontSize: '0.78rem' }}>—</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// HPR's address for notices — the same one the RCTIs and advance agreements carry.
const HPR_ADDRESS = 'PO Box 1710, Warriewood NSW 2102';

// A company's ABN is two check digits + its ACN, so a Pty Ltd's ACN is the ABN's last 9.
const acnFromAbn = (abn, companyName) => {
  const digits = String(abn || '').replace(/\D/g, '');
  if (digits.length !== 11 || !/\b(pty|ltd|limited)\b/i.test(companyName || '')) return '';
  return digits.slice(2).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
};

// ── Subcontractor agreement generator — HPR → subcontractor (driver company) ────
function SubcontractorTab({ initialDriverLogin }) {
  const { personnel, sheetData, addNotification } = useApp();
  const drivers = useMemo(
    () => personnel.filter(p => p.loginName).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [personnel],
  );

  const [driverId, setDriverId]         = useState('');
  const [subEntity, setSubEntity]       = useState('');
  const [subAbn, setSubAbn]             = useState('');
  const [subAcn, setSubAcn]             = useState('');
  const [subAddress, setSubAddress]     = useState('');
  const [hprAddress, setHprAddress]     = useState(HPR_ADDRESS);
  const [agreementDate, setAgreementDate]     = useState(toLocalISO(new Date()));
  const [commencementDate, setCommencementDate] = useState(toLocalISO(new Date()));
  const [expiryDate, setExpiryDate]     = useState(addMonthsISO(toLocalISO(new Date()), 12));
  const [deliveryArea, setDeliveryArea] = useState('');
  const [baseRate, setBaseRate]         = useState('');
  const [extraRates, setExtraRates]     = useState([]);   // [{label, amount}] ex-GST, from Driver Review
  const [dcaNumber, setDcaNumber]       = useState('');
  const [busy, setBusy]                 = useState('');
  const [hprSig, setHprSig]             = useState(null);
  const [showSigSetup, setShowSigSetup] = useState(false);
  const [tick, setTick]                 = useState(0);
  // Handoff from the Driver Review "Generate Contract" button (an accepted offer).
  const [prefill, setPrefill] = useState(() => { try { return JSON.parse(localStorage.getItem('hp-contract-prefill') || 'null'); } catch { return null; } });
  const [prefilledFrom, setPrefilledFrom] = useState('');

  useEffect(() => {
    fetch(AGREEMENT_URL, { method: 'POST', body: JSON.stringify({ action: 'getHprSignature' }) })
      .then(r => r.json()).then(d => { if (d.success) setHprSig(d.signature || null); }).catch(() => {});
    if (prefill && prefill.driverLogin) setDriverId(prefill.driverLogin);   // select the driver → the effect below applies the rates
  }, []); // eslint-disable-line

  // Deep-linked from Personnel "Generate" → pre-select that driver once the list is in
  // (skipped when a Driver-Review prefill already chose one).
  useEffect(() => {
    if (prefill && prefill.driverLogin) return;
    if (initialDriverLogin && drivers.some(d => d.loginName === initialDriverLogin)) setDriverId(initialDriverLogin);
  }, [initialDriverLogin, drivers]); // eslint-disable-line

  const driver = drivers.find(d => d.loginName === driverId) || null;

  // Default the subcontractor entity/ABN from the driver's personnel record, and the
  // per-parcel Base Rate from their assessed Driver Review price (the "Rates" sheet —
  // stored EX-GST, the same figure the RCTI uses). Still editable as an override.
  useEffect(() => {
    if (!driver) return;
    setSubEntity(driver.companyName || driver.name || '');
    setSubAbn(driver.abn || '');
    setSubAddress(driver.address || '');
    setSubAcn(acnFromAbn(driver.abn, driver.companyName));
    // A finalised offer was handed off for this driver → use its rates (override the
    // live Rates sheet) and consume the handoff so later driver switches use the sheet.
    if (prefill && (prefill.driverLogin || '').toUpperCase() === (driver.loginName || '').toUpperCase()) {
      setBaseRate(prefill.baseRate != null ? String(prefill.baseRate) : '');
      setExtraRates(Array.isArray(prefill.extraRates) ? prefill.extraRates : []);
      if (prefill.deliveryArea) setDeliveryArea(prefill.deliveryArea);
      setPrefilledFrom(prefill.driverName || driver.name || driver.loginName);
      try { localStorage.removeItem('hp-contract-prefill'); } catch {}
      setPrefill(null);
      return;
    }
    const rm = sheetData?.ratesMap || {};
    const login = driver.loginName || '';
    const r = rm[login] || rm[login.toUpperCase()] || {};
    const parcel = parseFloat(r.Parcel);
    setBaseRate(isFinite(parcel) && parcel > 0 ? String(parcel) : '');
    // Additional per-article rates from the same Rates sheet (all ex-GST).
    const RATE_FIELDS = [['Exp', 'Express'], ['DOE', 'DOE'], ['Depot Bags', 'Depot Bags']];
    setExtraRates(
      RATE_FIELDS
        .map(([key, label]) => ({ label, amount: parseFloat(r[key]) }))
        .filter((x) => isFinite(x.amount) && x.amount > 0),
    );
  }, [driverId, driver, sheetData]); // eslint-disable-line

  const baseRateNum = Number(baseRate) || 0;
  const rateFromReview = !!driver && baseRateNum > 0;

  const buildData = () => ({
    subcontractor: {
      entity: subEntity, abn: subAbn, acn: subAcn, address: subAddress,
      repName: driver?.name || '', repPosition: 'Director',
    },
    hprAddress, agreementDate, commencementDate, expiryDate, deliveryArea,
    baseRate: Number(baseRate) || 0, extraRates, dcaNumber, state: 'New South Wales',
    hprSignature: hprSig?.signature || null,
    hprSignatory: hprSig?.name || '',
    hprPosition: hprSig?.position || 'Director',
    hprSignedDate: hprSig?.signature ? toLocalISO(new Date()) : '',
  });

  const fileStem = () => `Subcontractor-Agreement-${(subEntity || driver?.name || 'sub').replace(/\s+/g, '')}`;

  const handlePreview = async () => {
    const win = window.open('', '_blank');
    setBusy('preview');
    try {
      const bytes = await buildSubcontractorAgreementPdfBytes(buildData());
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) win.location.href = url;
      else { const a = document.createElement('a'); a.href = url; a.download = `${fileStem()}.pdf`; a.click(); addNotification('Preview opened as a download (allow pop-ups to view in a tab)', 'info'); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); addNotification(`Couldn’t build the preview: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const handleDownload = async () => {
    setBusy('download');
    try {
      const bytes = await buildSubcontractorAgreementPdfBytes(buildData());
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${fileStem()}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { addNotification(`Download failed: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const handleSend = async () => {
    if (!driver?.loginName) { addNotification('Choose a driver first', 'error'); return; }
    if (!subEntity.trim()) { addNotification('Enter the subcontractor company name', 'error'); return; }
    if (!hprSig?.signature && !window.confirm('No HPR signature is on file, so the agreement will go out unsigned by HPR. Set it now via "HPR signature", or send anyway?')) return;
    setBusy('send');
    const send = (allowDuplicate) => fetch(AGREEMENT_URL, {
      method: 'POST',
      body: JSON.stringify({
        action: 'createAgreement',
        driverLogin: driver.loginName,
        driverName: driver.name || '',
        subEntity: subEntity.trim(), subAbn, subAcn, subAddress,
        subRepName: driver.name || '', subRepPosition: 'Director',
        hprAddress, agreementDate, commencementDate, expiryDate,
        deliveryArea, baseRate: Number(baseRate) || 0, extraRates, dcaNumber, state: 'New South Wales',
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
      }),
    }).then(r => r.json());
    try {
      let res = await send(false);
      if (res.duplicate && window.confirm(`${res.error}\n\nCreate another pending agreement anyway?`)) res = await send(true);
      if (res.success) { addNotification(`Sent to ${driver.name} — they’ll sign it in their portal`, 'success'); setTick(t => t + 1); }
      else if (!res.duplicate) addNotification(`Send failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const labelStyle = { fontSize: '0.78rem', fontWeight: 500, color: 'var(--hp-dim)', display: 'block', marginBottom: 5 };
  const ready = !!driver && !!subEntity.trim();

  return (
    <div>
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 24, alignItems: 'flex-start', marginBottom: 24 }}>
        <div className="card">
          <h4 style={{ marginBottom: 4 }}>Generate Subcontractor Agreement</h4>
          <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 14 }}>Hollypaul Recruitment Pty Ltd → Subcontractor</p>

          {prefilledFrom && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', marginBottom: 14, borderRadius: 8, background: 'color-mix(in srgb, var(--hp-green) 12%, var(--hp-surface))', border: '1px solid var(--hp-border)', fontSize: '0.8rem' }}>
              <CheckCircle size={15} color="var(--hp-green)" />
              <span style={{ flex: 1 }}>Base &amp; Schedule-1 rates pre-filled from <b>{prefilledFrom}</b>'s accepted offer. Review the legal details, then send.</span>
            </div>
          )}

          {/* HPR signature status — pre-signed onto every agreement (same saved signature as the leases) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', marginBottom: 16, borderRadius: 8, background: 'var(--hp-surface)', border: `1px solid ${hprSig?.signature ? 'var(--hp-border)' : 'var(--hp-amber)'}` }}>
            {hprSig?.signature ? <CheckCircle size={15} color="var(--hp-green)" /> : <AlertTriangle size={15} color="var(--hp-amber)" />}
            <span style={{ fontSize: '0.8rem', flex: 1 }}>HPR signature: {hprSig?.signature ? `on file (${hprSig.name || 'HPR'})` : 'not set'}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowSigSetup(true)}><PenLine size={12} /> {hprSig?.signature ? 'Update' : 'Set signature'}</button>
          </div>

          <div className="form-group">
            <label style={labelStyle}>Driver / Subcontractor</label>
            <select className="form-select" value={driverId} onChange={e => setDriverId(e.target.value)}>
              <option value="">— Choose driver —</option>
              {drivers.map(d => <option key={d.loginName} value={d.loginName}>{d.name}{d.abn ? ` (ABN ${d.abn})` : ''}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label style={labelStyle}>Subcontractor company name</label>
            <input className="form-input" value={subEntity} onChange={e => setSubEntity(e.target.value)} placeholder="e.g. EZ Transport Pty Ltd" />
          </div>
          <div className="form-group stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={labelStyle}>ABN</label><input className="form-input" value={subAbn} onChange={e => setSubAbn(e.target.value)} placeholder="ABN" /></div>
            <div><label style={labelStyle}>ACN (optional)</label><input className="form-input" value={subAcn} onChange={e => setSubAcn(e.target.value)} placeholder="ACN" /></div>
          </div>
          <div className="form-group">
            <label style={labelStyle}>Subcontractor address</label>
            <input className="form-input" value={subAddress} onChange={e => setSubAddress(e.target.value)} placeholder="Registered business address" />
          </div>
          <div className="form-group">
            <label style={labelStyle}>HPR address</label>
            <input className="form-input" value={hprAddress} onChange={e => setHprAddress(e.target.value)} placeholder="HPR registered address" />
          </div>
          <div className="form-group stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={labelStyle}>Commencement date</label><input className="form-input" type="date" value={commencementDate} onChange={e => { setCommencementDate(e.target.value); setExpiryDate(addMonthsISO(e.target.value, 12)); }} /></div>
            <div><label style={labelStyle}>Expiry date</label><input className="form-input" type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label style={labelStyle}>Delivery area (postcodes)</label>
            <input className="form-input" value={deliveryArea} onChange={e => setDeliveryArea(e.target.value)} placeholder="e.g. 2010, 2011, 2015" />
          </div>
          <div className="form-group stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Base rate ($/parcel, ex GST)</label>
              <input className="form-input" type="number" step="0.0001" value={baseRate} onChange={e => setBaseRate(e.target.value)} placeholder="e.g. 1.15" />
              {driver && (
                baseRateNum > 0
                  ? <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>{rateFromReview ? 'From Driver Review · ' : ''}{`${fmtRate(baseRateNum)} ex + ${fmtRate(baseRateNum * 0.1)} GST = ${fmtRate(baseRateNum * 1.1)} incl`}</div>
                  : <div style={{ fontSize: '0.72rem', color: 'var(--hp-amber)', marginTop: 4 }}>No per-parcel price on file — set one on the Driver Review page, or enter it here.</div>
              )}
            </div>
            <div><label style={labelStyle}>AP DCA number</label><input className="form-input" value={dcaNumber} onChange={e => setDcaNumber(e.target.value)} placeholder="PHBSYD-XXXX-XXX" /></div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary w-full" style={{ justifyContent: 'center' }} disabled={!ready || !!busy} onClick={handleSend}>
              {busy === 'send' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />} Send for e-signature
            </button>
            <button className="btn btn-secondary w-full" style={{ justifyContent: 'center' }} disabled={!ready || !!busy} onClick={handlePreview}>
              {busy === 'preview' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Eye size={14} />} Preview PDF
            </button>
            <button className="btn btn-ghost w-full" style={{ justifyContent: 'center' }} disabled={!ready || !!busy} onClick={handleDownload}>
              {busy === 'download' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />} Download PDF
            </button>
          </div>
        </div>

        <div className="card" style={{ background: 'var(--hp-surface)' }}>
          <h4 style={{ marginBottom: 16 }}>What the agreement carries</h4>
          {!driver ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--hp-muted)' }}>
              <FileSignature size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p style={{ fontSize: '0.875rem' }}>Choose a driver — the agreement pre-fills from their record.</p>
            </div>
          ) : (
            <div style={{ fontSize: '0.84rem', lineHeight: 1.8, color: 'var(--hp-text)' }}>
              <div><strong>Principal:</strong> Hollypaul Recruitment Pty Ltd (ABN 60 651 664 885)</div>
              <div><strong>Subcontractor:</strong> {subEntity || '—'}{subAbn ? ` · ABN ${subAbn}` : ''}</div>
              <div><strong>Signed by:</strong> {driver.name} (Director)</div>
              <div><strong>Term:</strong> {commencementDate ? fmtDate(commencementDate) : '—'} → {expiryDate ? fmtDate(expiryDate) : '—'}</div>
              <div><strong>Base rate (per parcel):</strong> {baseRateNum > 0 ? `${fmtRate(baseRateNum)} ex GST + ${fmtRate(baseRateNum * 0.1)} GST = ${fmtRate(baseRateNum * 1.1)} incl` : '—'}</div>
              {extraRates.map((r) => (
                <div key={r.label} style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>· {r.label}: {fmtRate(r.amount)} ex GST + {fmtRate(r.amount * 0.1)} GST = {fmtRate(r.amount * 1.1)} incl</div>
              ))}
              <div><strong>Delivery area:</strong> {deliveryArea || '—'}</div>
              <hr style={{ border: 'none', borderTop: '1px solid var(--hp-border)', margin: '12px 0' }} />
              <div style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>
                Award-105 (NSW GCCD) compliant. Personnel must be engaged as <strong>employees only</strong> and <strong>no further subcontracting</strong> is permitted (clause 6.1) — per Australia Post’s compliance requirements.
              </div>
            </div>
          )}
        </div>
      </div>

      {showSigSetup && (
        <LessorSignatureModal
          initial={hprSig}
          onClose={() => setShowSigSetup(false)}
          onSaved={(s) => setHprSig(s)}
          addNotification={addNotification}
        />
      )}

      <h4 style={{ margin: '4px 0 12px' }}>Subcontractor agreement register</h4>
      <SubcontractorRegister tick={tick} />
    </div>
  );
}

export default function ContractsPage() {
  // Deep link from Personnel: /contracts?tab=vehicle-lease|subcontractor&driver=LOGIN
  // opens that tab with the driver pre-selected so the build starts here.
  const [searchParams] = useSearchParams();
  const paramTab = searchParams.get('tab');
  const initialDriverLogin = searchParams.get('driver') || '';
  const [activeTab, setActiveTab] = useState(
    ['vehicle-lease', 'subcontractor', 'register', 'clients'].includes(paramTab) ? paramTab : 'vehicle-lease'
  );

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Contracts</h2>
          <p>Hollypaul Leasing vehicle leases · subcontractor agreements</p>
        </div>
      </div>

      <div className="page-body">
        <div className="tabs">
          {['vehicle-lease', 'subcontractor', 'register', 'clients'].map(t => (
            <button key={t} className={`tab${activeTab === t ? ' active' : ''}`} onClick={() => setActiveTab(t)}>
              {t === 'vehicle-lease' ? 'Vehicle Lease Agreements' : t === 'subcontractor' ? 'Subcontractor Agreements' : t === 'register' ? 'Contract Register' : 'Client Agreements'}
            </button>
          ))}
        </div>

        {activeTab === 'vehicle-lease' && <VehicleLeaseTab initialDriverLogin={initialDriverLogin} />}

        {/* ── Subcontractor Agreements ── */}
        {activeTab === 'subcontractor' && <SubcontractorTab initialDriverLogin={initialDriverLogin} />}

        {/* ── Contract Register ── */}
        {activeTab === 'register' && <ContractRegister />}

        {/* ── Client Agreements (signed on a client business's own site) ── */}
        {activeTab === 'clients' && <ClientAgreementsTab />}
      </div>
    </>
  );
}
